<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import Chart from 'chart.js/auto'

const props = defineProps({
  type:     { type: String,  default: 'bar' },
  labels:   { type: Array,   required: true },
  datasets: { type: Array,   required: true },
  height:   { type: Number,  default: 240 },
  options:  { type: Object,  default: () => ({}) },
})

const canvas = ref(null)
let chart = null
let observer = null
let rafHandle = null
let alive = false

const renderKey = computed(() => props.type + '|' + JSON.stringify(props.labels) + '|' + JSON.stringify(props.datasets))

function scheduleRender() {
  if (rafHandle) cancelAnimationFrame(rafHandle)
  rafHandle = requestAnimationFrame(() => {
    rafHandle = null
    if (!alive) return
    render()
  })
}

function render() {
  if (!alive) return
  const c = canvas.value
  if (!c) return
  const existing = Chart.getChart(c)
  if (existing) existing.destroy()
  if (chart) { chart.destroy(); chart = null }
  const ctx = c.getContext('2d')
  if (!ctx) return
  chart = new Chart(ctx, {
    type: props.type,
    data: { labels: props.labels, datasets: props.datasets },
    options: Object.assign({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: props.datasets.length > 1, labels: { boxWidth: 10, font: { size: 11 } } } },
      scales: props.type === 'doughnut' ? {} : {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { grid: { color: '#EEEBE1' }, ticks: { font: { size: 11 } } }
      }
    }, props.options)
  })
}

onMounted(() => {
  alive = true
  scheduleRender()
  if (canvas.value?.parentElement && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => {
      if (!alive) return
      if (chart) { try { chart.resize() } catch(e) {} }
      else scheduleRender()
    })
    observer.observe(canvas.value.parentElement)
  }
})

onBeforeUnmount(() => {
  alive = false
  if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = null }
  if (observer) { observer.disconnect(); observer = null }
  if (chart) { chart.destroy(); chart = null }
})

watch(renderKey, () => scheduleRender())
</script>

<template>
  <div :style="{ height: height + 'px', width: '100%' }">
    <canvas ref="canvas"></canvas>
  </div>
</template>
